import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/interactions/LoadingState";
import { cn } from "@/lib/utils";
import { getAuthenticatedClient } from "@/services";
import {
  Calendar,
  Plus,
  Check,
  Clock,
  AlertTriangle,
  ClipboardList,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  useVisitHistory,
  useScheduleVisit,
  type FarmVisit,
} from "@/hooks/useSupervisorData";

const visitTypeColors: Record<string, string> = {
  routine: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  biosecurity: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  follow_up: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  emergency: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  onboarding: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
};

const statusIcons: Record<string, React.ElementType> = {
  completed: Check,
  scheduled: Clock,
  overdue: AlertTriangle,
  in_progress: ClipboardList,
  cancelled: AlertTriangle,
};

export const VisitHistoryScreen: React.FC = () => {
  const { farmerId } = useParams<{ farmerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const client = getAuthenticatedClient();

  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [newVisitType, setNewVisitType] = useState("routine");
  const [newVisitDate, setNewVisitDate] = useState("");
  const [newVisitNotes, setNewVisitNotes] = useState("");

  const { data: visits = [], isLoading } = useVisitHistory(farmerId || "");
  const scheduleVisit = useScheduleVisit();

  // Fetch farmer profile
  const { data: farmer } = useQuery({
    queryKey: ["supervisor-farmer-profile", farmerId],
    queryFn: async () => {
      if (!farmerId) return null;
      const { data } = await client
        .from("profiles")
        .select("full_name, region, village, phone")
        .eq("id", farmerId)
        .single();
      return data;
    },
    enabled: !!farmerId,
  });

  const handleScheduleVisit = () => {
    if (!farmerId || !newVisitDate) return;
    scheduleVisit.mutate(
      {
        farmerId,
        visitType: newVisitType,
        scheduledDate: newVisitDate,
        notes: newVisitNotes || undefined,
      },
      {
        onSuccess: () => {
          setShowScheduleForm(false);
          setNewVisitDate("");
          setNewVisitNotes("");
          setNewVisitType("routine");
        },
      }
    );
  };

  const title = farmer?.full_name
    ? `${farmer.full_name}${farmer.region ? ` — ${farmer.region}` : ""}`
    : t("supervisor.visitHistory", "Visit History");

  return (
    <Screen topBarProps={{ title, showBack: true }}>
      <div className="p-4 space-y-4">
        {/* Schedule Visit FAB / Form */}
        <Button
          className="w-full"
          onClick={() => setShowScheduleForm(!showScheduleForm)}
        >
          {showScheduleForm ? (
            <ChevronUp className="w-4 h-4 mr-2" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          {t("supervisor.scheduleVisit", "Schedule Visit")}
        </Button>

        {showScheduleForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("supervisor.visitType", "Visit Type")}
                </label>
                <select
                  value={newVisitType}
                  onChange={(e) => setNewVisitType(e.target.value)}
                  className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  {["routine", "biosecurity", "follow_up", "emergency", "onboarding"].map(
                    (vt) => (
                      <option key={vt} value={vt}>
                        {t(`supervisor.${vt}`, vt.replace("_", " "))}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("common.date", "Date")}
                </label>
                <input
                  type="date"
                  value={newVisitDate}
                  onChange={(e) => setNewVisitDate(e.target.value)}
                  className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t("common.notes", "Notes")}
                </label>
                <textarea
                  value={newVisitNotes}
                  onChange={(e) => setNewVisitNotes(e.target.value)}
                  rows={2}
                  className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  placeholder={t("supervisor.visitNotesPlaceholder", "Optional visit notes...")}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleScheduleVisit}
                disabled={!newVisitDate || scheduleVisit.isPending}
              >
                {scheduleVisit.isPending
                  ? t("common.saving", "Saving...")
                  : t("common.confirm", "Confirm")}
              </Button>
            </Card>
          </motion.div>
        )}

        {/* Timeline */}
        {isLoading ? (
          <LoadingState message={t("common.loading", "Loading...")} />
        ) : visits.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            {t("supervisor.noVisits", "No visits recorded for this farmer")}
          </Card>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-border" />

            <div className="space-y-3">
              {visits.map((visit, idx) => (
                <VisitTimelineEntry key={visit.id} visit={visit} index={idx} t={t} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
};

const VisitTimelineEntry: React.FC<{
  visit: FarmVisit;
  index: number;
  t: ReturnType<typeof import("react-i18next").useTranslation>["t"];
}> = ({ visit, index, t }) => {
  const StatusIcon = statusIcons[visit.status] || Clock;
  const isCompleted = visit.status === "completed";

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative pl-10"
    >
      {/* Timeline dot */}
      <div
        className={cn(
          "absolute left-2.5 top-3 w-4 h-4 rounded-full border-2 border-background flex items-center justify-center",
          isCompleted ? "bg-green-500" : visit.status === "overdue" ? "bg-red-500" : "bg-blue-500"
        )}
      >
        <StatusIcon className="w-2.5 h-2.5 text-white" />
      </div>

      <Card className={cn("p-3 space-y-2", isCompleted && "opacity-80")}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">{visit.scheduled_date}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge
              className={cn(
                "text-[10px] px-1.5 py-0",
                visitTypeColors[visit.visit_type] || "bg-muted"
              )}
            >
              {t(`supervisor.${visit.visit_type}`, visit.visit_type.replace("_", " "))}
            </Badge>
            <Badge
              variant={isCompleted ? "default" : visit.status === "overdue" ? "destructive" : "outline"}
              className="text-[10px] px-1.5 py-0"
            >
              {t(`supervisor.${visit.status}`, visit.status)}
            </Badge>
          </div>
        </div>

        {visit.notes && (
          <p className="text-xs text-muted-foreground leading-relaxed">{visit.notes}</p>
        )}

        {visit.follow_up_required && visit.follow_up_date && (
          <div className="flex items-center gap-1 text-xs text-warning">
            <AlertTriangle className="w-3 h-3" />
            <span>
              Follow-up: {visit.follow_up_date}
            </span>
          </div>
        )}
      </Card>
    </motion.div>
  );
};
